import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Settings, Save, Sparkles, Globe, Share2, Building2, Users, Key,
  CheckCircle, AlertCircle, RefreshCcw, Palette, Link2, Lock, ExternalLink
} from 'lucide-react';
import { useBrandSettings, clearBrandSettingsCache } from '../../hooks/useBrandSettings';
import { useNavigate } from 'react-router-dom';
import SocialAccountsManager from '../../components/marketing/SocialAccountsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const socialPlatforms = [
  { key: 'facebook', name: 'Facebook', icon: '📘' },
  { key: 'instagram', name: 'Instagram', icon: '📷' },
  { key: 'linkedin', name: 'LinkedIn', icon: '💼' },
  { key: 'twitter', name: 'Twitter/X', icon: '🐦' },
  { key: 'google_business', name: 'Google Business', icon: '📍' }
];

export const MarketingSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('brand');
  
  // Phase 6: Fetch brand settings from Admin (read-only view)
  const { brandSettings, loading: brandLoading, refetch: refetchBrand } = useBrandSettings();

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/marketing/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
      // Set defaults
      setSettings({
        enable_check_ins: true,
        enable_blog_publishing: true,
        enable_social_publishing: true,
        enable_agency_module: true,
        enable_ai_generation: true,
        ai_provider: 'emergent',
        ai_model: 'gpt-4o',
        social_providers: {},
        website_base_url: '',
        website_blog_path: '/blog',
        website_check_in_path: '/our-work'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.patch(`${API}/marketing/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateSocialProvider = (platform, field, value) => {
    setSettings(prev => ({
      ...prev,
      social_providers: {
        ...prev.social_providers,
        [platform]: {
          ...prev.social_providers?.[platform],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <Settings className="h-8 w-8 text-slate-500" />
              Marketing Settings
            </h1>
            <p className="text-muted-foreground dark:text-slate-400 mt-1">
              Configure Marketing Hub features and integrations
            </p>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="dark:bg-slate-800">
            <TabsTrigger value="brand">Brand Kit</TabsTrigger>
            <TabsTrigger value="general">Features</TabsTrigger>
            <TabsTrigger value="pacing">Pacing & Goals</TabsTrigger>
            <TabsTrigger value="ai">AI Provider</TabsTrigger>
            <TabsTrigger value="social">Social Platforms</TabsTrigger>
            <TabsTrigger value="website">Website Publishing</TabsTrigger>
            <TabsTrigger value="agency">Agency (SSW)</TabsTrigger>
          </TabsList>

          {/* Phase 6: Brand Kit (Synced from Admin Settings) */}
          <TabsContent value="brand" className="mt-6 space-y-4">
            <Card className="dark:bg-slate-800/60 border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 dark:text-white">
                      <Palette className="h-5 w-5 text-blue-500" />
                      Brand Kit
                      <Badge variant="secondary" className="ml-2 text-xs">
                        <Link2 className="h-3 w-3 mr-1" />
                        Synced from Admin
                      </Badge>
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Brand assets are managed centrally in Admin Settings
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        clearBrandSettingsCache();
                        refetchBrand();
                        toast.success('Brand settings refreshed');
                      }}
                      disabled={brandLoading}
                    >
                      <RefreshCcw className={`h-4 w-4 mr-2 ${brandLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/settings/company')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Edit in Admin
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {brandLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <>
                    {/* Company Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Company Name</Label>
                        <div className="mt-1 p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg flex items-center justify-between">
                          <span className="dark:text-white font-medium">{brandSettings?.company_name || 'Not set'}</span>
                          <Lock className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Default Font</Label>
                        <div className="mt-1 p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg flex items-center justify-between">
                          <span className="dark:text-white" style={{ fontFamily: brandSettings?.default_font }}>{brandSettings?.default_font || 'Inter'}</span>
                          <Lock className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    </div>

                    {/* Primary Colors */}
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground mb-3 block">Primary & Secondary Colors</Label>
                      <div className="flex gap-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-lg border-2 border-slate-300 dark:border-slate-600"
                            style={{ backgroundColor: brandSettings?.primary_color || '#2563eb' }}
                          />
                          <div>
                            <p className="text-sm font-medium dark:text-white">Primary</p>
                            <p className="text-xs text-muted-foreground">{brandSettings?.primary_color || '#2563eb'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-lg border-2 border-slate-300 dark:border-slate-600"
                            style={{ backgroundColor: brandSettings?.secondary_color || '#1e40af' }}
                          />
                          <div>
                            <p className="text-sm font-medium dark:text-white">Secondary</p>
                            <p className="text-xs text-muted-foreground">{brandSettings?.secondary_color || '#1e40af'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Brand Colors */}
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground mb-3 block">Brand Color Palette (Content Studio)</Label>
                      <div className="flex flex-wrap gap-2">
                        {(brandSettings?.brand_colors || []).map((color, idx) => (
                          <div key={idx} className="flex flex-col items-center gap-1">
                            <div 
                              className="w-10 h-10 rounded-lg border-2 border-slate-300 dark:border-slate-600"
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                            <span className="text-[10px] text-muted-foreground">{color}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Marketing Channels */}
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground mb-3 block">Default Marketing Channels</Label>
                      <div className="flex flex-wrap gap-2">
                        {(brandSettings?.marketing_channels || []).map((channel, idx) => (
                          <Badge key={idx} variant="secondary" className="capitalize">
                            {channel}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Logo */}
                    {brandSettings?.logo_url && (
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground mb-3 block">Company Logo</Label>
                        <div className="p-4 bg-slate-100 dark:bg-slate-700/50 rounded-lg inline-block">
                          <img 
                            src={brandSettings.logo_url.startsWith('http') ? brandSettings.logo_url : `${BACKEND_URL}${brandSettings.logo_url}`}
                            alt="Company Logo"
                            className="h-12 object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800 dark:text-blue-300">Single Source of Truth</p>
                  <p className="text-blue-700 dark:text-blue-400 mt-1">
                    Brand settings are managed in <strong>Admin → Company Settings</strong> and automatically sync to:
                  </p>
                  <ul className="text-blue-600 dark:text-blue-400 mt-2 space-y-1 list-disc list-inside">
                    <li>Content Studio (brand colors for design elements)</li>
                    <li>Marketing Dashboard (company name, logo)</li>
                    <li>Scheduler (channel labels)</li>
                    <li>Asset metadata defaults</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* General Settings */}
          <TabsContent value="general" className="mt-6 space-y-4">
            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="dark:text-white">Feature Flags</CardTitle>
                <CardDescription className="dark:text-slate-400">Enable or disable Marketing Hub features</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="dark:text-white">Geo Check-Ins</Label>
                    <p className="text-sm text-muted-foreground dark:text-slate-400">Property check-in and showcase feature</p>
                  </div>
                  <Switch
                    checked={settings?.enable_check_ins}
                    onCheckedChange={(v) => updateSetting('enable_check_ins', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="dark:text-white">Blog Publishing</Label>
                    <p className="text-sm text-muted-foreground dark:text-slate-400">Blog post creation and publishing</p>
                  </div>
                  <Switch
                    checked={settings?.enable_blog_publishing}
                    onCheckedChange={(v) => updateSetting('enable_blog_publishing', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="dark:text-white">Social Publishing</Label>
                    <p className="text-sm text-muted-foreground dark:text-slate-400">Social media post creation and scheduling</p>
                  </div>
                  <Switch
                    checked={settings?.enable_social_publishing}
                    onCheckedChange={(v) => updateSetting('enable_social_publishing', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="dark:text-white">Agency Module</Label>
                    <p className="text-sm text-muted-foreground dark:text-slate-400">So Shall We agency integration</p>
                  </div>
                  <Switch
                    checked={settings?.enable_agency_module}
                    onCheckedChange={(v) => updateSetting('enable_agency_module', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="dark:text-white">AI Content Generation</Label>
                    <p className="text-sm text-muted-foreground dark:text-slate-400">AI-assisted content creation</p>
                  </div>
                  <Switch
                    checked={settings?.enable_ai_generation}
                    onCheckedChange={(v) => updateSetting('enable_ai_generation', v)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pacing & Goals Settings */}
          <TabsContent value="pacing" className="mt-6 space-y-4">
            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Building2 className="h-5 w-5 text-blue-500" />
                  Company Monthly Goals
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Set monthly marketing targets. These drive pacing calculations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Volume Goals */}
                <div>
                  <Label className="text-xs font-semibold uppercase text-muted-foreground mb-3 block">Volume Targets</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="dark:text-slate-200">Monthly Leads</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 500"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.leads || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, leads: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-200">Monthly Appointments</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 200"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.appointments || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, appointments: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-200">Monthly Sales</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 50"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.sales || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, sales: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-200">Monthly Revenue ($)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 250000"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.revenue || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, revenue: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Rate Goals */}
                <div>
                  <Label className="text-xs font-semibold uppercase text-muted-foreground mb-3 block">Efficiency Targets</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="dark:text-slate-200">Close Rate (%)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 35"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.close_rate || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, close_rate: parseFloat(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Target % of demos that close</p>
                    </div>
                    <div>
                      <Label className="dark:text-slate-200">Average Ticket ($)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 5500"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.avg_ticket || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, avg_ticket: parseInt(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Target revenue per sale</p>
                    </div>
                    <div>
                      <Label className="dark:text-slate-200">Marketing Spend ($)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 25000"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.spend || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, spend: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-200">Cost of Marketing (%)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 10"
                        className="dark:bg-slate-700 mt-1"
                        value={settings?.pacing_goals?.marketing_cost_percent || ''}
                        onChange={(e) => updateSetting('pacing_goals', { ...settings?.pacing_goals, marketing_cost_percent: parseFloat(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Target spend as % of revenue</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                  Pacing Status Thresholds
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Define when KPIs show green (on track), yellow (watch), or red (off pace)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="dark:text-slate-200 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      Green (On Track) - Above %
                    </Label>
                    <Input
                      type="number"
                      placeholder="90"
                      className="dark:bg-slate-700 mt-1"
                      value={settings?.pacing_thresholds?.green || 90}
                      onChange={(e) => updateSetting('pacing_thresholds', { ...settings?.pacing_thresholds, green: parseInt(e.target.value) || 90 })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">e.g., 90% = if pacing at 90%+ of goal</p>
                  </div>
                  <div>
                    <Label className="dark:text-slate-200 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                      Yellow (Watch) - Above %
                    </Label>
                    <Input
                      type="number"
                      placeholder="75"
                      className="dark:bg-slate-700 mt-1"
                      value={settings?.pacing_thresholds?.yellow || 75}
                      onChange={(e) => updateSetting('pacing_thresholds', { ...settings?.pacing_thresholds, yellow: parseInt(e.target.value) || 75 })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Below this = Red (Off Pace)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <RefreshCcw className="h-5 w-5 text-purple-500" />
                  Manual Data Import
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Import marketing data when API integrations are not available
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
                    <CardContent className="py-4">
                      <h4 className="font-medium text-sm mb-2">Marketing Data</h4>
                      <p className="text-xs text-muted-foreground mb-3">Spend, leads, appointments, sales by market</p>
                      <div className="space-y-2">
                        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => window.open('/api/marketing/pacing/templates/marketing.csv', '_blank')}>
                          Download Sample CSV
                        </Button>
                        <Button variant="default" size="sm" className="w-full text-xs">
                          Import Marketing Data
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
                    <CardContent className="py-4">
                      <h4 className="font-medium text-sm mb-2">Website Analytics</h4>
                      <p className="text-xs text-muted-foreground mb-3">Sessions, conversions, traffic sources</p>
                      <div className="space-y-2">
                        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => window.open('/api/marketing/pacing/templates/website.csv', '_blank')}>
                          Download Sample CSV
                        </Button>
                        <Button variant="default" size="sm" className="w-full text-xs">
                          Import Website Data
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
                    <CardContent className="py-4">
                      <h4 className="font-medium text-sm mb-2">Call Data</h4>
                      <p className="text-xs text-muted-foreground mb-3">Calls, answer rates, bookings</p>
                      <div className="space-y-2">
                        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => window.open('/api/marketing/pacing/templates/calls.csv', '_blank')}>
                          Download Sample CSV
                        </Button>
                        <Button variant="default" size="sm" className="w-full text-xs">
                          Import Call Data
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-xs">
                  <p className="font-medium text-blue-800 dark:text-blue-300 mb-1">Import Instructions:</p>
                  <ul className="text-blue-700 dark:text-blue-400 space-y-0.5 list-disc list-inside">
                    <li>Date format must be YYYY-MM-DD (e.g., 2026-01-15)</li>
                    <li>Market ID must match existing markets in the system</li>
                    <li>Duplicate records (same date/market/source) will be updated</li>
                    <li>Leave optional fields blank if unknown</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Provider Settings */}
          <TabsContent value="ai" className="mt-6 space-y-4">
            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  AI Provider Configuration
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Configure AI services for content generation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="dark:text-slate-200">AI Provider</Label>
                  <Select
                    value={settings?.ai_provider || 'emergent'}
                    onValueChange={(v) => updateSetting('ai_provider', v)}
                  >
                    <SelectTrigger className="dark:bg-slate-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emergent">Emergent (Universal Key)</SelectItem>
                      <SelectItem value="openai">OpenAI (Custom Key)</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Custom Key)</SelectItem>
                      <SelectItem value="gemini">Google Gemini (Custom Key)</SelectItem>
                    </SelectContent>
                  </Select>
                  {settings?.ai_provider === 'emergent' && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Using Emergent Universal Key (pre-configured)
                    </p>
                  )}
                </div>

                <div>
                  <Label className="dark:text-slate-200">Model</Label>
                  <Select
                    value={settings?.ai_model || 'gpt-4o'}
                    onValueChange={(v) => updateSetting('ai_model', v)}
                  >
                    <SelectTrigger className="dark:bg-slate-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-4o">GPT-4o (Recommended)</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini (Faster)</SelectItem>
                      <SelectItem value="claude-sonnet">Claude Sonnet</SelectItem>
                      <SelectItem value="gemini-pro">Gemini Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings?.ai_provider !== 'emergent' && (
                  <div>
                    <Label className="dark:text-slate-200">Custom API Key</Label>
                    <Input
                      type="password"
                      value={settings?.ai_api_key || ''}
                      onChange={(e) => updateSetting('ai_api_key', e.target.value)}
                      placeholder="Enter your API key"
                      className="dark:bg-slate-700 mt-1"
                    />
                    <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                      Required when using a custom provider
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Platform Settings */}
          <TabsContent value="social" className="mt-6 space-y-4">
            {/* Phase 8A: Social Accounts Manager */}
            <SocialAccountsManager />
          </TabsContent>

          {/* Website Publishing Settings */}
          <TabsContent value="website" className="mt-6 space-y-4">
            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Globe className="h-5 w-5 text-emerald-500" />
                  Website Publishing Configuration
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Configure settings for publishing content to your website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="dark:text-slate-200">Website Base URL</Label>
                  <Input
                    value={settings?.website_base_url || ''}
                    onChange={(e) => updateSetting('website_base_url', e.target.value)}
                    placeholder="https://www.rhinoshieldflorida.com"
                    className="dark:bg-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="dark:text-slate-200">Blog Path</Label>
                  <Input
                    value={settings?.website_blog_path || '/blog'}
                    onChange={(e) => updateSetting('website_blog_path', e.target.value)}
                    placeholder="/blog"
                    className="dark:bg-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="dark:text-slate-200">Check-In / Our Work Path</Label>
                  <Input
                    value={settings?.website_check_in_path || '/our-work'}
                    onChange={(e) => updateSetting('website_check_in_path', e.target.value)}
                    placeholder="/our-work"
                    className="dark:bg-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="dark:text-slate-200">Website API Key</Label>
                  <Input
                    type="password"
                    value={settings?.website_api_key || ''}
                    onChange={(e) => updateSetting('website_api_key', e.target.value)}
                    placeholder="API key for website integration"
                    className="dark:bg-slate-700 mt-1"
                  />
                  <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                    Required for automated publishing to your website
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agency (SSW) Settings */}
          <TabsContent value="agency" className="mt-6 space-y-4">
            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Building2 className="h-5 w-5 text-orange-500" />
                  So Shall We Agency Integration
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Configure connection settings for SSW agency
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-300">Placeholder Configuration</h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                        SSW API integration is pending. Currently using internal request tracking.
                        API credentials and webhook endpoints will be configured here once available.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <Label className="dark:text-slate-200">SSW API Endpoint</Label>
                    <Input
                      value=""
                      placeholder="https://api.soshallwe.com/v1"
                      disabled
                      className="dark:bg-slate-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-slate-200">API Key</Label>
                    <Input
                      type="password"
                      value=""
                      placeholder="Pending SSW API integration"
                      disabled
                      className="dark:bg-slate-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-slate-200">Webhook URL (Inbound)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={`${BACKEND_URL}/api/marketing/webhooks/ssw`}
                        readOnly
                        className="dark:bg-slate-700"
                      />
                      <Button variant="outline" onClick={() => {
                        navigator.clipboard.writeText(`${BACKEND_URL}/api/marketing/webhooks/ssw`);
                        toast.success('Webhook URL copied!');
                      }}>
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                      Provide this URL to SSW for webhook notifications
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
